/**
 * 라우트 레지스트리 — 타입이 붙은 단일 원천 (ADR 0023)
 * --------------------------------------------------------------------------
 * **정적 라우트 사실의 유일한 표**입니다. 라우터·좌측 nav·breadcrumb·Command
 * Palette가 모두 이 배열 하나를 읽습니다.
 *
 * 이 파일은 **leaf 모듈**입니다 — 화면 컴포넌트를 import하지 않습니다. 그래야
 * breadcrumb(`components/drill.tsx`)처럼 화면이 다시 import하는 모듈도 순환
 * 없이 같은 표를 읽을 수 있습니다. element 결합은 `routes.tsx`가 합니다.
 *
 * 규칙
 * - **경로 집합을 바꾸지 않습니다.** 기존 라우트를 옮겨 담은 것이고 추가·삭제·
 *   리다이렉트가 없습니다. `/resources`·`/deployments`·`/secrets`는 서로 다른
 *   화면으로 그대로 남습니다.
 * - **capability는 여기서 선언만 합니다.** 실제 권한은 서버가 강제하고
 *   (`/api/v1/scope`), 이 값은 "노출 여부"의 기존 규칙을 그대로 옮긴 것입니다.
 * - catch-all(`*`)만 이 레지스트리 밖에 남습니다 — 라우팅이 아니라 리다이렉트라서
 *   목록의 다른 항목과 성격이 다릅니다.
 * - **확장 라우트는 여기에 손으로 적지 않습니다.** 매니페스트가 정본이고
 *   (`packages/extensions/manifests`), 생성된 기여가 코어 표와 합쳐집니다. (ADR 0024)
 */
import { extensionRouteMetas, mergeRouteMeta } from "@/app/extensions/contributions";

/** 좌측 nav의 그룹. 기존 AppShell의 그룹 순서를 그대로 옮겼습니다. */
export type RouteGroup = "observe" | "resources" | "manage" | "custom";

/**
 * 노출 조건. 이름은 `/api/v1/scope` 응답 필드와 1:1입니다.
 * `always`는 클러스터 접근 권한만 있으면 보이는 화면입니다.
 */
export type RouteCapability = "always" | "canExploreResources" | "canManageWorkloads" | "canManageExecSessions" | "canViewGitOps";

/** 화면 하나의 라우트 사실. element를 뺀 전부가 여기 있습니다. */
export interface RouteMeta {
  /** 안정적인 식별자. 팔레트 결과 key·breadcrumb 조회·테스트가 이 값을 씁니다. */
  id: string;
  /** react-router path. 파라미터 세그먼트를 포함합니다. */
  path: string;
  /**
   * 사람이 읽는 이름의 **폴백**. 아직 다국어로 옮기지 않은 화면(breadcrumb)과
   * 확장 기여가 이 값을 씁니다. 코어 라우트에서는 ko 카탈로그 값과 글자 단위로
   * 같아야 하며, i18n 게이트가 그 동일성을 강제합니다. (ADR 0025)
   */
  label: string;
  /**
   * 다국어 메시지 ID(`nav.route.<id>`). nav·팔레트가 이 ID로 라벨을 고릅니다.
   * 확장 기여에는 없으며, 그때는 `label`이 그대로 쓰입니다. (ADR 0025)
   */
  labelId?: string;
  group: RouteGroup;
  capability: RouteCapability;
  /**
   * NavLink `end` 의미. 루트(`/`)만 정확 일치가 필요합니다 —
   * 없으면 모든 경로에서 활성으로 보입니다.
   */
  end?: boolean;
  /**
   * 좌측 nav와 팔레트의 이동 목록에 나타날지.
   * 파라미터가 필요한 상세 화면은 목적지를 만들 수 없으므로 false입니다.
   */
  nav: boolean;
  /**
   * 이 라우트를 준 확장 id(ADR 0024). 코어 라우트는 undefined입니다.
   * 값이 있으면 서버가 그 확장을 등록했다고 답해야만 노출됩니다.
   */
  extensionId?: string;
}

/** 코어 라우트 사실. 확장이 준 라우트는 여기 없습니다. */
const CORE_ROUTE_META: RouteMeta[] = [
  { id: "overview", path: "/", label: "Cluster Overview", labelId: "nav.route.overview", group: "observe", capability: "always", end: true, nav: true },
  { id: "nodes", path: "/nodes", label: "Nodes", labelId: "nav.route.nodes", group: "observe", capability: "always", nav: true },
  { id: "namespaces", path: "/namespaces", label: "Namespaces", labelId: "nav.route.namespaces", group: "observe", capability: "always", nav: true },
  { id: "namespace-detail", path: "/namespaces/:namespace", label: "Namespace 상세", labelId: "nav.route.namespace-detail", group: "observe", capability: "always", nav: false },
  /* Workload·Pod 상세는 namespace **경로 아래**에 있습니다. (#111)
     namespace는 전역 선택이 아니라 이 리소스의 신원이라 URL에 남아야 하고, 쿼리로 실으면
     "선택된 namespace는 query-params에 넣지 않는다"(#94)와 겉보기에 충돌합니다. 경로로
     올리면 쿼리에서 사라지면서 링크 공유·새 탭·북마크가 그대로 같은 리소스를 엽니다.
     옛 `?ns=` 링크는 `legacyDetailRedirect`가 이 경로로 replace 이동시킵니다. */
  { id: "workload-detail", path: "/namespaces/:namespace/workloads/:kind/:name", label: "Workload 상세", labelId: "nav.route.workload-detail", group: "observe", capability: "always", nav: false },
  { id: "pod-detail", path: "/namespaces/:namespace/pods/:name", label: "Pod 상세", labelId: "nav.route.pod-detail", group: "observe", capability: "always", nav: false },
  { id: "topology", path: "/topology", label: "Pod Topology", labelId: "nav.route.topology", group: "observe", capability: "always", nav: true },
  /* Logs Explorer는 확장 기여입니다 — 매니페스트에서 옵니다. (ADR 0024) */
  /* Warning Events 전용 화면(#208). 기존 `core/v1 Event` Warning watch 하나를 그대로
     읽는 **조회 전용** 화면이며 direct 모드에서만 데이터가 있습니다. */
  { id: "events", path: "/events", label: "Warning Events", labelId: "nav.route.events", group: "observe", capability: "always", nav: true },
  { id: "alerts", path: "/alerts", label: "Alerts", labelId: "nav.route.alerts", group: "observe", capability: "always", nav: true },
  { id: "workloads", path: "/workloads", label: "Workloads", labelId: "nav.route.workloads", group: "observe", capability: "always", nav: true },

  /* Resources 진입점(ADR 0018). 아래 관리 라우트는 그대로 둡니다 —
     탭에서 이동하는 대상이자 기존 링크·북마크의 목적지입니다. */
  { id: "resources", path: "/resources", label: "Resources", labelId: "nav.route.resources", group: "resources", capability: "canExploreResources", nav: true },
  { id: "gitops", path: "/gitops", label: "GitOps Applications", labelId: "nav.route.gitops", group: "resources", capability: "canViewGitOps", nav: true },
  /* `/resource-map`은 **비-nav 진입점**(ADR 0020 · M4-I13 결정 1)입니다 — 새
     화면이 아니라 질의를 보존한 채 `tab=map`으로 `/resources`에 대체 이동합니다.
     nav·팔레트 목적지에 나타나지 않고, 권한 경계는 ResourcesView의 것입니다. */
  { id: "resource-map", path: "/resource-map", label: "Resource Map 진입점", labelId: "nav.route.resource-map", group: "resources", capability: "canExploreResources", nav: false },
  { id: "deployments", path: "/deployments", label: "Deployments", labelId: "nav.route.deployments", group: "manage", capability: "canManageWorkloads", nav: true },
  { id: "secrets", path: "/secrets", label: "Secrets", labelId: "nav.route.secrets", group: "manage", capability: "canManageWorkloads", nav: true },
  { id: "debug-sessions", path: "/debug-sessions", label: "Debug Sessions", labelId: "nav.route.debug-sessions", group: "manage", capability: "canManageExecSessions", nav: true },

  { id: "dashboard", path: "/dashboards/:id", label: "Dashboard", labelId: "nav.route.dashboard", group: "custom", capability: "always", nav: false },
  { id: "dashboard-builder", path: "/dashboard-builder", label: "Dashboard Builder", labelId: "nav.route.dashboard-builder", group: "custom", capability: "always", nav: true },
  { id: "dashboard-builder-editor", path: "/dashboard-builder/:id", label: "Dashboard Builder 편집", labelId: "nav.route.dashboard-builder-editor", group: "custom", capability: "always", nav: false },

  /* 노드 상세(#207). `group`은 observe이지만 **표의 맨 끝**에 둡니다.
     이 배열의 위치는 단순한 나열 순서가 아니라 nav 순위입니다(`mergeRouteMeta`가
     index × CORE_ORDER_STEP로 순위를 만들고, 확장이 선언한 order가 그 사이에
     끼어듭니다). `nodes` 바로 뒤에 끼우면 이후 모든 코어 라우트의 순위가 한 칸씩
     밀려 Logs Explorer(order 65)가 Pod Topology 앞으로 올라갑니다 — 요청하지 않은
     좌측 nav 순서 변경입니다. 이 라우트는 `nav: false`라 목록에 나타나지 않으므로
     맨 끝에 두어도 화면 동작은 같고, 기존 순서는 그대로 보존됩니다.

     노드 상세는 **클러스터 스코프**라 namespace 경로 아래가 아닙니다. 신원은
     이름 + UID이고 UID는 `?uid=`로 실립니다 — 이름은 재사용되므로 경로만으로는
     재생성된 노드와 이전 노드를 구분할 수 없습니다. */
  { id: "node-detail", path: "/nodes/:name", label: "Node 상세", labelId: "nav.route.node-detail", group: "observe", capability: "always", nav: false },
  /* 파라미터 상세는 nav 순위를 바꾸지 않도록 표 끝에 둡니다. node-detail과 같은 이유입니다. */
  { id: "manage-workload", path: "/namespaces/:namespace/workloads/:kind/:name/manage", label: "Workload 관리", labelId: "nav.route.manage-workload", group: "manage", capability: "canManageWorkloads", nav: false },
  { id: "gitops-detail", path: "/gitops/applications/:uid", label: "GitOps Application 상세", labelId: "nav.route.gitops-detail", group: "resources", capability: "canViewGitOps", nav: false },
];

/**
 * 코어 표 + 확장 기여. 확장은 선언한 nav order로 코어 사이에 들어가므로,
 * 새 확장을 더해도 이 파일을 고칠 일이 없습니다.
 */
export const ROUTE_META: RouteMeta[] = mergeRouteMeta<RouteMeta>(CORE_ROUTE_META, extensionRouteMetas());

/**
 * 그룹 표시 순서와 제목. 순서는 기존 AppShell 그대로이고, 제목은 `titleId`로
 * 고릅니다. `title`은 카탈로그가 아직 없을 때의 폴백이며 ko 값과 같습니다. (ADR 0025)
 */
export const NAV_GROUPS: { id: RouteGroup; title: string; titleId: string }[] = [
  { id: "observe", title: "관측", titleId: "nav.group.observe" },
  { id: "resources", title: "리소스", titleId: "nav.group.resources" },
  { id: "manage", title: "관리", titleId: "nav.group.manage" },
  { id: "custom", title: "Custom", titleId: "nav.group.custom" },
];

/** 서버가 준 capability. 이 값이 없으면(로딩 중) 닫힌 쪽으로 답합니다. */
export interface RouteCapabilities {
  canExploreResources: boolean;
  canManageWorkloads: boolean;
  /** Debug Sessions 관리 화면 노출 여부(platform.admin + 세션 레지스트리). 없으면 닫힘. (ADR 0030) */
  canManageExecSessions?: boolean;
  canViewGitOps?: boolean;
  /**
   * 서버가 등록했다고 답한 확장 id(ADR 0024). **없으면 빈 집합으로 봅니다** —
   * 아직 모르는 확장을 nav·팔레트에 먼저 띄우지 않습니다(fail closed).
   */
  enabledExtensions?: ReadonlySet<string>;
}

export function routeAllowed(route: Pick<RouteMeta, "capability" | "extensionId">, caps: RouteCapabilities): boolean {
  /* 확장 라우트는 서버가 그 확장을 등록했다고 답할 때만 목적지가 됩니다. */
  if (route.extensionId !== undefined && !(caps.enabledExtensions?.has(route.extensionId) ?? false)) return false;
  switch (route.capability) {
    case "canExploreResources":
      return caps.canExploreResources;
    case "canManageWorkloads":
      return caps.canManageWorkloads;
    case "canManageExecSessions":
      return caps.canManageExecSessions ?? false;
    case "canViewGitOps":
      return caps.canViewGitOps ?? false;
    default:
      return true;
  }
}

/** id로 라우트 사실을 찾습니다. 없는 id는 호출부의 오타이므로 undefined입니다. */
export function routeMetaById(id: string): RouteMeta | undefined {
  return ROUTE_META.find((r) => r.id === id);
}

/**
 * breadcrumb 한 칸을 레지스트리에서 만듭니다.
 *
 * 라벨을 화면마다 손으로 적으면 nav는 바뀌고 breadcrumb만 옛 이름으로 남습니다.
 * 같은 표에서 읽으면 그 어긋남 자체가 생기지 않습니다. 파라미터가 있는 경로는
 * 목적지를 만들 수 없으므로 `to` 없이 라벨만 돌려줍니다.
 */
export function routeCrumb(id: string, to?: string): { label: string; to?: string } {
  const meta = routeMetaById(id);
  const label = meta?.label ?? id;
  if (to === undefined || meta === undefined || meta.path.includes(":")) return { label };
  return { label, to };
}

/** 좌측 nav와 팔레트가 함께 쓰는 목적지 목록. 순서는 레지스트리 순서입니다. */
export function navRoutes(caps: RouteCapabilities): RouteMeta[] {
  return ROUTE_META.filter((r) => r.nav && routeAllowed(r, caps));
}

export function navRoutesInGroup(group: RouteGroup, caps: RouteCapabilities): RouteMeta[] {
  return navRoutes(caps).filter((r) => r.group === group);
}
